import React, { useState, useEffect, useCallback } from 'react';
import { Grid3x3, Plus, RefreshCw, Loader, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { stcBinService } from '../../../../services/stcBinService';
import { RackSummary, BinLocation, FilterState, FilterOptions, RackStructure } from './types';
import { RackGrid } from './RackGrid';
import { CreateRackDialog } from './CreateRackDialog';
import { BulkUpdateModal } from './BulkUpdateModal';
import { FilterPanel } from './FilterPanel';
import { BackButton } from '../../../common/BackButton';

/**
 * Reolbygger / Warehouse Builder Application (FR-101 - FR-108)
 *
 * Optimized for large screens to display maximum locations
 * Layout: 2-column layout (filter panel + grid view)
 * Bulk update operations now in modal dialog (BulkUpdateModal)
 * Grid-view is the dominant element that controls page height
 */

interface RackBuilderAppProps {
  onBack?: () => void;
}

export const RackBuilderApp: React.FC<RackBuilderAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();
    const [racks, setRacks] = useState<RackSummary[]>([]);
    const [selectedRackNumbers, setSelectedRackNumbers] = useState<string[]>([]);
    const [rackStructures, setRackStructures] = useState<Map<string, RackStructure>>(new Map());
    const [allLocations, setAllLocations] = useState<BinLocation[]>([]);
    const [filteredLocations, setFilteredLocations] = useState<BinLocation[]>([]);
    const [selectedBinCodes, setSelectedBinCodes] = useState<string[]>([]);
    const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCreateRackModal, setShowCreateRackModal] = useState(false);
    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
    const [isRackPanelExpanded, setIsRackPanelExpanded] = useState(true);

    // Default filter state with extended properties
    const [filterState, setFilterState] = useState<FilterState>({
        binSizeNo: [],
        binTypeNo: [],
        resourceNo: [],
        stcZoneID: [],
        binGrpNo: [],
        stcBinCatNo: [],
        pickPriorityNo: [],
        pickableYesNo: 'all',
        itemNo: 'all',
        // Extended filter state
        putAwayPriorityNo: [],
        reservationAllowed: 'all',
        autoReplenishment: 'all',
        negativeReservationAllowed: 'all',
        sharedBinYesNo: 'all',
        sharedBinPreferEmptyBin: 'all',
        replenishMinLevelFrom: 0,
        replenishMinLevelTo: 9999,
        replenishMaxLevelFrom: 0,
        replenishMaxLevelTo: 9999,
        customSortIDFrom: 0,
        customSortIDTo: 9999,
        sharedBinMaxQuantityFrom: 0,
        sharedBinMaxQuantityTo: 9999
    });

    const companyNo = user?.companyNo || '';
    const warehouseID = currentWarehouse || '1';

    // Load racks on mount
    useEffect(() => {
        loadRacks();
        loadFilterOptions();
    }, [companyNo, warehouseID]);

    /**
     * Load all racks for warehouse (FR-001)
     */
    const loadRacks = async () => {
        if (!companyNo) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const data = await stcBinService.getRackSummary(companyNo, warehouseID);
            setRacks(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load racks');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load filter options (FR-005)
     */
    const loadFilterOptions = async () => {
        if (!companyNo) return;
        
        try {
            const options = await stcBinService.getFilterOptions(companyNo, warehouseID);
            setFilterOptions(options);
        } catch (err) {
            console.error('Failed to load filter options:', err);
        }
    };

    /**
     * Load locations for selected racks (FR-002)
     */
    const loadLocationsForSelectedRacks = async () => {
        if (selectedRackNumbers.length === 0) {
            setAllLocations([]);
            setFilteredLocations([]);
            setRackStructures(new Map());
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            const allLocs: BinLocation[] = [];
            const structures = new Map<string, RackStructure>();
            
            for (const rackNumber of selectedRackNumbers) {
                const structure = await stcBinService.getRackStructure(companyNo, warehouseID, rackNumber);
                structures.set(rackNumber, structure);
                allLocs.push(...structure.locations);
            }
            
            setRackStructures(structures);
            setAllLocations(allLocs);
            // Apply current filter
            await applyFilter(allLocs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load locations');
        } finally {
            setIsLoading(false);
        }
    };

    // Reload locations when selected racks change
    useEffect(() => {
        loadLocationsForSelectedRacks();
        // Clear selection when racks change
        setSelectedBinCodes([]);
    }, [selectedRackNumbers]);

    /**
     * Apply current filter (FR-004) - Updated with extended filter properties
     */
    const applyFilter = async (locations = allLocations) => {
        try {
            const request = {
                companyNo,
                warehouseID,
                rackNumbers: selectedRackNumbers.length > 0 ? selectedRackNumbers : undefined,
                binSizeNo: filterState.binSizeNo.length > 0 ? filterState.binSizeNo : undefined,
                binTypeNo: filterState.binTypeNo.length > 0 ? filterState.binTypeNo : undefined,
                resourceNo: filterState.resourceNo.length > 0 ? filterState.resourceNo : undefined,
                stcZoneID: filterState.stcZoneID.length > 0 ? filterState.stcZoneID : undefined,
                binGrpNo: filterState.binGrpNo.length > 0 ? filterState.binGrpNo : undefined,
                stcBinCatNo: filterState.stcBinCatNo.length > 0 ? filterState.stcBinCatNo : undefined,
                pickPriorityNo: filterState.pickPriorityNo.length > 0 ? filterState.pickPriorityNo : undefined,
                pickableYesNo: filterState.pickableYesNo === 'yes' ? true : filterState.pickableYesNo === 'no' ? false : undefined,
                hasItemNo: filterState.itemNo === 'has' ? true : filterState.itemNo === 'empty' ? false : undefined,
                // Extended filter properties
                putAwayPriorityNo: filterState.putAwayPriorityNo.length > 0 ? filterState.putAwayPriorityNo : undefined,
                reservationAllowed: filterState.reservationAllowed === 'yes' ? true : filterState.reservationAllowed === 'no' ? false : undefined,
                autoReplenishment: filterState.autoReplenishment === 'yes' ? true : filterState.autoReplenishment === 'no' ? false : undefined,
                negativeReservationAllowed: filterState.negativeReservationAllowed === 'yes' ? true : filterState.negativeReservationAllowed === 'no' ? false : undefined,
                sharedBinYesNo: filterState.sharedBinYesNo === 'yes' ? true : filterState.sharedBinYesNo === 'no' ? false : undefined,
                sharedBinPreferEmptyBin: filterState.sharedBinPreferEmptyBin === 'yes' ? true : filterState.sharedBinPreferEmptyBin === 'no' ? false : undefined,
                replenishMinLevelFrom: filterState.replenishMinLevelFrom > 0 ? filterState.replenishMinLevelFrom : undefined,
                replenishMinLevelTo: filterState.replenishMinLevelTo < 9999 ? filterState.replenishMinLevelTo : undefined,
                replenishMaxLevelFrom: filterState.replenishMaxLevelFrom > 0 ? filterState.replenishMaxLevelFrom : undefined,
                replenishMaxLevelTo: filterState.replenishMaxLevelTo < 9999 ? filterState.replenishMaxLevelTo : undefined,
                customSortIDFrom: filterState.customSortIDFrom > 0 ? filterState.customSortIDFrom : undefined,
                customSortIDTo: filterState.customSortIDTo < 9999 ? filterState.customSortIDTo : undefined,
                sharedBinMaxQuantityFrom: filterState.sharedBinMaxQuantityFrom > 0 ? filterState.sharedBinMaxQuantityFrom : undefined,
                sharedBinMaxQuantityTo: filterState.sharedBinMaxQuantityTo < 9999 ? filterState.sharedBinMaxQuantityTo : undefined
            };

            const filtered = await stcBinService.filterBins(request);
            setFilteredLocations(filtered);
        } catch (err) {
            console.error('Failed to filter bins:', err);
            setFilteredLocations(locations);
        }
    };

    /**
     * Handle rack selection
     */
    const handleRackSelect = (rackNumber: string, isCtrlKey: boolean) => {
        if (isCtrlKey) {
            // Multi-select
            setSelectedRackNumbers(prev => 
                prev.includes(rackNumber)
                    ? prev.filter(r => r !== rackNumber)
                    : [...prev, rackNumber]
            );
        } else {
            // Single select
            setSelectedRackNumbers([rackNumber]);
        }
    };

    /**
     * Handle location selection in grid
     */
    const handleLocationSelect = (binCode: string, isCtrlKey: boolean, isShiftKey: boolean) => {
        if (isShiftKey && selectedBinCodes.length > 0) {
            // Range select
            const lastSelected = selectedBinCodes[selectedBinCodes.length - 1];
            const lastIndex = filteredLocations.findIndex(l => l.binCode === lastSelected);
            const currentIndex = filteredLocations.findIndex(l => l.binCode === binCode);
            
            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const range = filteredLocations.slice(start, end + 1).map(l => l.binCode);
                setSelectedBinCodes(prev => Array.from(new Set([...prev, ...range])));
            }
        } else if (isCtrlKey) {
            // Multi-select
            setSelectedBinCodes(prev =>
                prev.includes(binCode)
                    ? prev.filter(b => b !== binCode)
                    : [...prev, binCode]
            );
        } else {
            // Single select
            setSelectedBinCodes([binCode]);
        }
    };

    /**
     * Select all visible locations
     */
    const handleSelectAll = () => {
        setSelectedBinCodes(filteredLocations.map(l => l.binCode));
    };

    /**
     * Clear all selections
     */
    const handleClearSelection = () => {
        setSelectedBinCodes([]);
    };

    /**
     * Reset all filters to default values
     */
    const handleResetFilters = () => {
        setFilterState({
            binSizeNo: [],
            binTypeNo: [],
            resourceNo: [],
            stcZoneID: [],
            binGrpNo: [],
            stcBinCatNo: [],
            pickPriorityNo: [],
            pickableYesNo: 'all',
            itemNo: 'all',
            // Extended filter state
            putAwayPriorityNo: [],
            reservationAllowed: 'all',
            autoReplenishment: 'all',
            negativeReservationAllowed: 'all',
            sharedBinYesNo: 'all',
            sharedBinPreferEmptyBin: 'all',
            replenishMinLevelFrom: 0,
            replenishMinLevelTo: 9999,
            replenishMaxLevelFrom: 0,
            replenishMaxLevelTo: 9999,
            customSortIDFrom: 0,
            customSortIDTo: 9999,
            sharedBinMaxQuantityFrom: 0,
            sharedBinMaxQuantityTo: 9999
        });
    };

    // Reapply filter when filter state changes
    useEffect(() => {
        if (allLocations.length > 0) {
            applyFilter();
        }
    }, [filterState]);

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            {/* Toolbar - Compact */}
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack || (() => {})} />
                    <Grid3x3 className="w-5 h-5 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">Reolbygger</h1>
                        <p className="text-xs text-gray-600">
                            Lager: {warehouseID} | Selskap: {companyNo}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => setShowCreateRackModal(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Ny Reol</span>
                    </button>
                    
                    <button
                        onClick={loadRacks}
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        <span>Oppdater</span>
                    </button>
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex-shrink-0">
                    {error}
                </div>
            )}

            {/* Rack selector - COLLAPSIBLE RIBBON STYLE */}
            <div className="bg-white border-b border-gray-200 flex-shrink-0">
                {/* Ribbon header - always visible */}
                <div 
                    className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setIsRackPanelExpanded(!isRackPanelExpanded)}
                >
                    <div className="flex items-center space-x-3">
                        <h2 className="text-sm font-semibold text-gray-800">
                            Velg Reoler
                        </h2>
                        {selectedRackNumbers.length > 0 && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                {selectedRackNumbers.length} valgt
                            </span>
                        )}
                        {!isRackPanelExpanded && selectedRackNumbers.length > 0 && (
                            <span className="text-xs text-gray-600">
                                {selectedRackNumbers.map(r => `Reol ${r}`).join(', ')}
                            </span>
                        )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        {selectedRackNumbers.length > 0 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRackNumbers([]);
                                }}
                                className="text-xs text-gray-600 hover:text-gray-800 underline"
                            >
                                Fjern alle
                            </button>
                        )}
                        {isRackPanelExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </div>
                </div>

                {/* Rack grid - collapsible */}
                {isRackPanelExpanded && (
                    <div className="px-4 pb-3 border-t border-gray-100 animate-in slide-in-from-top duration-200">
                        {isLoading && racks.length === 0 ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="ml-2 text-sm text-gray-600">Laster reoler...</span>
                            </div>
                        ) : racks.length === 0 ? (
                            <div className="text-center py-4 text-sm text-gray-500">
                                Ingen reoler funnet. Opprett en ny reol for å komme i gang.
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 xl:grid-cols-16 gap-2 mt-3">
                                {racks.map(rack => (
                                    <button
                                        key={rack.rackNumber}
                                        onClick={(e) => handleRackSelect(rack.rackNumber, e.ctrlKey || e.metaKey)}
                                        className={`p-2 rounded border-2 transition-all text-left ${
                                            selectedRackNumbers.includes(rack.rackNumber)
                                                ? 'bg-blue-100 border-blue-500 shadow-sm'
                                                : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow'
                                        }`}
                                    >
                                        <div className="text-sm font-bold text-gray-800">
                                            Reol {rack.rackNumber}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {rack.locationCount} lok
                                        </div>
                                        <div className="text-[10px] text-gray-500">
                                            V{rack.minVertical}-{rack.maxVertical} × H{rack.minShelf}-{rack.maxShelf}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Main content area - 2 columns with FIXED HEIGHT based on viewport */}
            {selectedRackNumbers.length > 0 && (
                <div
                    className="flex-1 min-h-0 flex gap-2 p-2"
                    style={{ maxHeight: isRackPanelExpanded ? 'calc(100vh - 240px)' : 'calc(100vh - 160px)' }}
                >
                    {/* Filter panel (Left) - WITH SCROLL */}
                    <div className="w-64 bg-white rounded-lg shadow flex flex-col flex-shrink-0">
                        <div className="bg-white border-b border-gray-200 px-3 py-2 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">Filtrer</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            <FilterPanel
                                filterOptions={filterOptions}
                                filterState={filterState}
                                onFilterChange={setFilterState}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </div>

                    {/* Grid view (Center) - DOMINANT ELEMENT that fills space */}
                    <div className="flex-1 bg-white rounded-lg shadow flex flex-col min-w-0">
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">
                                Lokasjoner {filteredLocations.length > 0 && `(${filteredLocations.length})`}
                            </h3>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handleSelectAll}
                                    disabled={filteredLocations.length === 0}
                                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                                >
                                    Velg alle
                                </button>
                                <button
                                    onClick={handleClearSelection}
                                    disabled={selectedBinCodes.length === 0}
                                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    Fjern valg
                                </button>
                                <button
                                    onClick={() => setShowBulkUpdateModal(true)}
                                    disabled={selectedBinCodes.length === 0}
                                    className="flex items-center space-x-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Settings className="w-4 h-4" />
                                    <span>Vedlikehold lokasjoner</span>
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex-1 overflow-hidden p-2">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-sm text-gray-600">Laster lokasjoner...</span>
                                </div>
                            ) : filteredLocations.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-sm text-gray-500">
                                    Ingen lokasjoner funnet
                                </div>
                            ) : (
                                <div className="h-full space-y-4 overflow-y-auto">
                                    {/* Show each selected rack */}
                                    {selectedRackNumbers.map(rackNumber => {
                                        const structure = rackStructures.get(rackNumber);
                                        if (!structure) return null;

                                        return (
                                            <div key={rackNumber} className="h-full flex flex-col min-h-0">
                                                <h4 className="font-semibold text-sm text-gray-800 mb-2 flex-shrink-0">
                                                    Reol {rackNumber}
                                                </h4>
                                                <div className="flex-1 min-h-0">
                                                    <RackGrid
                                                        rackStructure={structure}
                                                        selectedBinCodes={selectedBinCodes}
                                                        onSelectionChange={setSelectedBinCodes}
                                                        filteredBinCodes={filteredLocations.map(l => l.binCode)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Rack Modal (FR-106) */}
            {showCreateRackModal && (
                <CreateRackDialog
                    warehouseID={warehouseID}
                    onClose={() => setShowCreateRackModal(false)}
                    onSuccess={() => {
                        loadRacks();
                        loadLocationsForSelectedRacks();
                    }}
                />
            )}

            {/* Bulk Update Modal */}
            <BulkUpdateModal
                isOpen={showBulkUpdateModal}
                onClose={() => setShowBulkUpdateModal(false)}
                selectedBinCodes={selectedBinCodes}
                warehouseID={warehouseID}
                filterOptions={filterOptions}
                onUpdateSuccess={() => {
                    loadLocationsForSelectedRacks();
                    setSelectedBinCodes([]);
                    setShowBulkUpdateModal(false);
                }}
                onDeleteSuccess={() => {
                    loadRacks();
                    loadLocationsForSelectedRacks();
                    setSelectedBinCodes([]);
                    setShowBulkUpdateModal(false);
                }}
            />
        </div>
    );
};
